import React, { useState } from 'react';

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
} from '@mui/material';

import DateTimeWidget from '~/components/Common/Elements/DateTimeWidget';
import PageHeader from '~/components/Common/Elements/PageHeader';
import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { ROLE_SEARCH_CURATOR } from '~/consts/roles';

import useAppNavigation from '~/hooks/useAppNavigation';

import SearchService, { EvVariant } from '~/services/SearchService';
import { formatDateISO } from '~/services/TimeService';

import { getSearchOrderingRegionCodes } from '~/utils/getSearchOrderingRegionCodes';

const ERROR_MESSAGE_NO_REGION_SELECTED = 'Please select at least one region';
const ERROR_MESSAGE_NO_SCORE = 'Please input an override score';
const ERROR_MESSAGE_NO_EXPIRE_DATE = 'Please pickup an expire date';
const ERROR_MESSAGE_INVALID_EXPIRE_DATE = 'Please input a valid expire date';
const REGION_LIST_COLUMN_COUNT = 4;

interface Props {
  offerScores: Array<App.OfferScore>;
  match: any;
}

const regionCodes = getSearchOrderingRegionCodes();

function SearchOrderingEditContainer({ match }: Props) {
  const offerId = match.params.id_offer;
  const vertical = match.params.vertical;
  const { push } = useAppNavigation();

  const [checkedRegions, setCheckedRegions] = useState<Array<string>>([]);
  const [expireDate, setExpireDate] = useState<string | null>(null);
  const [score, setScore] = useState<number | undefined>();
  const [errors, setErrors] = useState<Array<string>>([ERROR_MESSAGE_NO_REGION_SELECTED, ERROR_MESSAGE_NO_EXPIRE_DATE]);
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);

  const isAllChecked = regionCodes.length > 0 && checkedRegions.length === regionCodes.length;
  const isBedBankOffer = vertical === 'hotel' && offerId.length === 36;
  const getBackPath = (): string => {
    switch (vertical) {
      case 'experience':
        return `/experience/${offerId}`;
      case 'cruise':
        return `/cruises/offer/${offerId}`;
      default:
        return isBedBankOffer ? `/bedbank/properties/${offerId}` : `/offers/${offerId}`;
    }
  };
  const backPath = getBackPath();

  const handleRegionSelect = (region: string) => () => {
    if (region === 'all') {
      setCheckedRegions(checkedRegions.length === regionCodes.length ? [] : regionCodes);
      return;
    }

    const currentIndex = checkedRegions.indexOf(region);
    const newChecked = [...checkedRegions];

    if (currentIndex === -1) {
      newChecked.push(region);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setCheckedRegions(newChecked);
  };

  const validateScore = (event) => {
    setScore(event.target.value === '' ? undefined : event.target.value);
  };

  const validateForm = (): Array<string> => {
    const newErrors = [];
    if (score === undefined) {
      newErrors.push(ERROR_MESSAGE_NO_SCORE);
    }

    if (!checkedRegions.length) {
      newErrors.push(ERROR_MESSAGE_NO_REGION_SELECTED);
    }

    if (expireDate === null) {
      newErrors.push(ERROR_MESSAGE_NO_EXPIRE_DATE);
    } else if (expireDate === 'Invalid Date') {
      newErrors.push(ERROR_MESSAGE_INVALID_EXPIRE_DATE);
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = async () => {
    const newErrors = validateForm();
    if (newErrors.length > 0) {
      setShowErrorMessage(true);
      return;
    }
    const overrideExpireDate = formatDateISO(expireDate);
    const payload = checkedRegions.map((region) => ({
      region,
      variant: EvVariant.Default,
      overrideScore: score,
      overrideExpireDate,
    }));

    await SearchService.patchOfferScore(vertical, offerId, payload);

    push(backPath);
  };

  const content = (
    <>
      {showErrorMessage && errors.length > 0 && <Alert severity="error">{errors.join('; ')}</Alert>}
      <Container>
        <Button
          component={Link}
          href={backPath}
          variant="text"
          startIcon={<ArrowBackIosNewIcon />}
          color="secondary"
          size="large"
        >
          Return to offer
        </Button>
        <PageHeader title="Edit Search Ordering" />
        <Alert severity="info">
          <Stack direction="row" spacing={10} sx={{ width: '100%' }}>
            <Box sx={{ flex: 1 }}>
              Enter the Final Ranking Score you want, we will convert it behind the scenes to a fixed percentile
              (meaning that the resulting score should be close but may not match exactly).
              <br />
              <br />
              Go to the{' '}
              <Link
                href="https://admin.luxuryescapes.com/search/search-ranking"
                target="_blank"
                rel="noopener noreferrer"
              >
                Search Ranking page
              </Link>{' '}
              to find the current final ranking scores in the last column.
            </Box>
            <Box sx={{ flex: 1 }}>
              For example if the top offers have these final scores:
              <br />
              - 1.934
              <br />
              - 1.913
              <br />
              - 1.910
              <br />
              - 1.893
              <br />
              and you want the boosted offer to be at position 2, set a Final Ranking Score between 1.934 and 1.913
            </Box>
          </Stack>
        </Alert>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" mt={2}>
          <TextField
            id="override-score-input"
            label="Override final score"
            type="number"
            value={score ?? ''}
            onChange={validateScore}
          />
          <DateTimeWidget
            value={expireDate}
            label="Override Expire Date"
            showDateOnly
            disablePast
            fullWidth={false}
            onChange={setExpireDate}
          />
        </Stack>
        <Box mt={2}>
          <ListItemButton role={undefined} onClick={handleRegionSelect('all')} dense>
            <ListItemIcon>
              <Checkbox
                checked={isAllChecked}
                indeterminate={checkedRegions.length > 0 && checkedRegions.length < regionCodes.length}
                edge="start"
                tabIndex={-1}
                disableRipple
                inputProps={{ 'aria-labelledby': 'all' }}
              />
            </ListItemIcon>
            <ListItemText id="all" primary="Select All Regions" />
          </ListItemButton>

          <Divider />

          <List
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${REGION_LIST_COLUMN_COUNT}, minmax(0, 1fr))`,
            }}
          >
            {regionCodes.map((regionCode) => {
              const labelId = `checkbox-list-label-${regionCode}`;
              return (
                <ListItem key={regionCode} disablePadding>
                  <ListItemButton role={undefined} onClick={handleRegionSelect(regionCode)} dense>
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={checkedRegions.includes(regionCode)}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{ 'aria-labelledby': labelId }}
                      />
                    </ListItemIcon>
                    <ListItemText id={labelId} primary={regionCode} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button variant="contained" color="primary" onClick={handleSubmit}>
            Submit
          </Button>
        </Box>
      </Container>
    </>
  );

  if (vertical !== 'hotel') {
    return content;
  }

  return (
    <PermissionedComponent
      requiredRoles={[ROLE_SEARCH_CURATOR]}
      fallback={
        <Container>
          <Alert severity="error">You do not have permission to edit hotel search ordering.</Alert>
        </Container>
      }
    >
      {content}
    </PermissionedComponent>
  );
}

export default SearchOrderingEditContainer;
